<template>
  <div class="roles-list">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Admin / Motivos de pendiente</p>
            <h1 class="list-title">Motivos de pendiente</h1>
            <p class="list-subtitle">
              Administre el catálogo de motivos que indican por qué un trámite
              no ha iniciado o no se ha liquidado.
            </p>
          </div>
        </v-flex>

        <v-flex xs12>
          <v-card class="roles-panel">
            <v-card-text class="roles-card__toolbar">
              <v-layout row wrap align-center>
                <v-flex md8 sm8 xs12>
                  <v-text-field
                    v-model="search"
                    clearable
                    flat
                    hide-details
                    placeholder="Buscar por nombre..."
                    solo
                    class="roles-search"
                    append-icon="search"
                    @keyup.enter="loadReasons"
                    @click:clear="onSearchClear"
                    @click:append="loadReasons"
                  ></v-text-field>
                </v-flex>
                <v-flex md4 sm4 xs12 class="toolbar-actions">
                  <v-btn
                    @click="openDialog()"
                    color="#e5252a"
                    dark
                    class="btn-nuevo"
                  >
                    <v-icon left>add</v-icon>Nuevo motivo
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-card-text class="pa-0 roles-content-wrap">
              <v-data-table
                v-if="!$vuetify.breakpoint.smAndDown"
                class="roles-table"
                :headers="headers"
                :items="reasons"
                :loading="loading"
                :pagination.sync="pagination"
                :rows-per-page-items="rowsPerPageItems"
                rows-per-page-text="Filas por pagina:"
                no-data-text="No hay motivos disponibles"
              >
                <template slot="items" slot-scope="props">
                  <td>
                    <div class="role-name-cell">
                      <span
                        class="role-color-dot"
                        :style="{ background: '#c79a2b' }"
                      ></span>
                      {{ props.item.name }}
                    </div>
                  </td>
                  <td>
                    <v-chip
                      :class="
                        props.item.active
                          ? 'status-chip status-chip--active'
                          : 'status-chip status-chip--inactive'
                      "
                      text-color="white"
                      small
                    >
                      {{ props.item.active ? 'Activo' : 'Inactivo' }}
                    </v-chip>
                  </td>
                  <td>
                    <v-btn
                      @click="openDialog(props.item)"
                      class="table-action-btn table-action-btn--edit"
                      flat
                      icon
                      small
                      title="Editar motivo"
                    >
                      <v-icon>edit</v-icon>
                    </v-btn>
                    <v-btn
                      @click="openDeleteModal(props.item)"
                      class="table-action-btn table-action-btn--delete"
                      flat
                      icon
                      small
                      title="Eliminar motivo"
                    >
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </td>
                </template>
                <template slot="pageText" slot-scope="props">
                  {{ props.pageStart }}-{{ props.pageStop }} de
                  {{ props.itemsLength }}
                </template>
              </v-data-table>

              <div v-else class="roles-mobile-list">
                <v-container fluid class="pa-3">
                  <v-layout row wrap>
                    <v-flex xs12 v-if="!reasons.length && !loading">
                      <v-alert type="info" value="true" class="mb-0">
                        No hay motivos disponibles.
                      </v-alert>
                    </v-flex>
                    <v-flex
                      xs12
                      v-for="reason in paginatedReasons"
                      :key="reason.id"
                      class="mb-3"
                    >
                      <v-card class="role-mobile-card">
                        <div
                          class="role-mobile-card__head"
                          :style="{ '--role-color': '#c79a2b' }"
                        >
                          <div class="role-mobile-card__head-main">
                            <p class="role-mobile-card__title">
                              {{ reason.name }}
                            </p>
                          </div>
                          <v-chip
                            :class="
                              reason.active
                                ? 'status-chip status-chip--active'
                                : 'status-chip status-chip--inactive'
                            "
                            text-color="white"
                            small
                          >
                            {{ reason.active ? 'Activo' : 'Inactivo' }}
                          </v-chip>
                        </div>
                        <div class="role-mobile-card__actions">
                          <v-btn
                            small
                            dark
                            color="#254f87"
                            class="mobile-action-btn"
                            @click="openDialog(reason)"
                          >
                            <v-icon small left>edit</v-icon>Editar
                          </v-btn>
                          <v-btn
                            small
                            dark
                            color="#e5252a"
                            class="mobile-action-btn"
                            @click="openDeleteModal(reason)"
                          >
                            <v-icon small left>delete</v-icon>Eliminar
                          </v-btn>
                        </div>
                      </v-card>
                    </v-flex>
                  </v-layout>
                  <div v-if="totalPages > 1" class="mobile-pagination">
                    <v-pagination
                      v-model="pagination.page"
                      :length="totalPages"
                      :total-visible="5"
                      color="#e5252a"
                    ></v-pagination>
                  </div>
                </v-container>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="600" persistent v-model="dialog">
      <v-card class="role-dialog-card">
        <v-card-title class="role-dialog-title">
          <v-icon left color="#254f87">{{
            editing.id ? 'edit' : 'add_circle'
          }}</v-icon>
          {{ editing.id ? 'Editar motivo' : 'Nuevo motivo' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-form ref="form">
            <v-text-field
              v-model="editing.name"
              label="Nombre del motivo"
              placeholder="Ej: Pendiente de Certificados"
              required
              :rules="[v => !!v || 'El nombre es requerido']"
            ></v-text-field>
            <v-checkbox
              v-model="editing.active"
              label="Activo"
              color="#1f9d63"
            ></v-checkbox>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="dialog = false" flat color="#254f87">Cancelar</v-btn>
          <v-btn @click="saveReason" color="#e5252a" dark :loading="saving">
            <v-icon left small>save</v-icon>Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog max-width="350" persistent v-model="loading">
      <v-card>
        <v-card-text class="text-center py-4">
          <v-progress-circular
            color="#e5252a"
            indeterminate
          ></v-progress-circular>
          <p class="mt-2">Cargando...</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <delete-confirm-dialog
      v-model="deleteDialog.show"
      :max-width="460"
      title="Eliminar motivo"
      subtitle="Esta acción no se puede deshacer"
      :message="`¿Estás seguro de que deseas eliminar ${deleteDialog.name}?`"
      warning="Si el motivo está en uso no se podrá eliminar; desactívalo en su lugar."
      confirm-text="Sí, eliminar"
      @confirm="deleteReason"
    ></delete-confirm-dialog>

    <v-snackbar
      v-model="snack.show"
      :timeout="3500"
      :color="snack.color"
      right
      top
    >
      {{ snack.message }}
      <v-btn flat dark @click="snack.show = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import Api from '@/api/index'
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog'

function extractList(resp) {
  if (!resp) return []
  if (Array.isArray(resp)) return resp
  if (Array.isArray(resp.items)) return resp.items
  if (resp.data) {
    if (Array.isArray(resp.data)) return resp.data
    if (Array.isArray(resp.data.items)) return resp.data.items
  }
  return []
}

export default {
  name: 'AdminPendingReasons',
  components: {
    DeleteConfirmDialog,
  },
  data() {
    return {
      reasons: [],
      search: '',
      dialog: false,
      loading: false,
      saving: false,
      pagination: {
        page: 1,
        rowsPerPage: 10,
        sortBy: 'name',
        descending: false,
      },
      rowsPerPageItems: [10, 25, 50, { text: 'Todos', value: -1 }],
      headers: [
        { text: 'Motivo', value: 'name' },
        { text: 'Estado', value: 'active' },
        { text: 'Acciones', sortable: false },
      ],
      editing: {
        id: null,
        name: '',
        active: true,
      },
      deleteDialog: {
        show: false,
        id: null,
        name: 'este motivo',
      },
      snack: { show: false, color: 'info', message: '' },
    }
  },
  computed: {
    paginatedReasons() {
      if (this.pagination.rowsPerPage === -1) return this.reasons
      const start = (this.pagination.page - 1) * this.pagination.rowsPerPage
      return this.reasons.slice(start, start + this.pagination.rowsPerPage)
    },
    totalPages() {
      if (this.pagination.rowsPerPage === -1) return 1
      return Math.max(
        1,
        Math.ceil(this.reasons.length / this.pagination.rowsPerPage)
      )
    },
  },
  mounted() {
    document.body.classList.add('admin-flush-top')
    this.loadReasons()
  },
  beforeDestroy() {
    document.body.classList.remove('admin-flush-top')
  },
  methods: {
    notify(message, color = 'info') {
      this.snack = { show: true, color, message }
    },
    isHttpError(response) {
      return !!(
        response &&
        typeof response.status === 'number' &&
        response.status >= 400
      )
    },
    async loadReasons() {
      this.loading = true
      try {
        const term = (this.search || '').trim()
        const response = await Api.PendingReason.getAll({ term })
        this.reasons = extractList(response)
      } catch (error) {
        this.notify('Error al cargar los motivos', 'error')
      } finally {
        this.loading = false
      }
    },
    onSearchClear() {
      this.search = ''
      this.loadReasons()
    },
    openDialog(reason = null) {
      if (reason) {
        this.editing = {
          id: reason.id,
          name: reason.name,
          active: reason.active !== false,
        }
      } else {
        this.editing = {
          id: null,
          name: '',
          active: true,
        }
      }
      this.dialog = true
    },
    async saveReason() {
      if (!this.$refs.form.validate()) return
      this.saving = true
      try {
        const payload = {
          id: this.editing.id,
          name: this.editing.name,
          active: this.editing.active,
        }
        const response = await Api.PendingReason.save(payload)
        if (this.isHttpError(response)) {
          this.notify(
            (response.data && response.data.error) ||
              'Error al guardar el motivo',
            'error'
          )
          return
        }
        this.notify('Motivo guardado correctamente', 'success')
        this.dialog = false
        await this.loadReasons()
      } catch (error) {
        this.notify('Error al guardar el motivo', 'error')
      } finally {
        this.saving = false
      }
    },
    openDeleteModal(reason) {
      this.deleteDialog = {
        show: true,
        id: reason.id,
        name: reason && reason.name ? reason.name : 'este motivo',
      }
    },
    async deleteReason() {
      const id = this.deleteDialog.id
      if (id == null) return
      this.deleteDialog.show = false
      try {
        const response = await Api.PendingReason.delete(id)
        if (this.isHttpError(response)) {
          this.notify(
            (response.data && response.data.error) ||
              'No se pudo eliminar el motivo',
            'error'
          )
          return
        }
        this.notify('Motivo eliminado correctamente', 'success')
        await this.loadReasons()
      } catch (error) {
        this.notify('No se pudo eliminar el motivo', 'error')
      }
    },
  },
}
</script>

<style scoped>
.roles-list {
  --brand-primary: #e5252a;
  --brand-secondary: #254f87;
  --brand-accent: #c79a2b;
  --surface: #ffffff;
  --surface-soft: #f6f8fc;
  --text-primary: #172133;
  --text-muted: #5f6880;
  --border-soft: #e4e8f1;
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 16px;
}

.list-hero {
  padding: 4px 0 8px;
}

.list-crumb {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.list-title {
  margin: 4px 0;
  font-size: 32px;
  line-height: 1.1;
  color: #0f172a;
  font-weight: 700;
}

.list-subtitle {
  margin: 0;
  font-size: 14px;
  color: #475569;
}

.roles-panel {
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  background: #ffffff;
}

.roles-card__toolbar {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.roles-search {
  max-width: 100%;
}

.btn-nuevo {
  border-radius: 8px;
}

.roles-content-wrap {
  background: var(--surface-soft);
}

.roles-table {
  background: var(--surface);
}

.roles-table ::v-deep thead th {
  color: #64748b !important;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  background: #f8fafc;
}

.roles-table ::v-deep tbody td {
  color: #334155;
  font-size: 14px;
  border-bottom: 1px solid #eef2f7;
}

.roles-table ::v-deep tbody tr:hover td {
  background: #f8fafc;
}

.role-name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.role-color-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-chip {
  font-weight: 700;
}

.status-chip--active {
  background-color: #1f9d63 !important;
}

.status-chip--inactive {
  background-color: #d94848 !important;
}

.table-action-btn {
  margin: 0 2px;
}

.table-action-btn--edit {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}

.table-action-btn--delete {
  border: 1px solid rgba(229, 37, 42, 0.3) !important;
  color: #e5252a !important;
  background: rgba(229, 37, 42, 0.1) !important;
  box-shadow: none !important;
}

.roles-mobile-list {
  background: var(--surface-soft);
}

.role-mobile-card {
  border-radius: 14px;
  border: 1px solid var(--border-soft);
  box-shadow: 0 10px 28px rgba(20, 37, 70, 0.08);
  overflow: hidden;
}

.role-mobile-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 14px;
  background: linear-gradient(
    135deg,
    var(--role-color, #254f87),
    rgba(0, 0, 0, 0.18)
  );
  color: #ffffff;
}

.role-mobile-card__head-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.role-mobile-card__title {
  margin: 0;
  font-weight: 700;
  font-size: 15px;
  color: #ffffff;
}

.role-mobile-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px;
  flex-wrap: wrap;
}

.mobile-action-btn {
  border-radius: 9px;
}

.mobile-pagination {
  display: flex;
  justify-content: center;
  padding: 12px 0 4px;
}

.mobile-pagination ::v-deep .v-pagination__item--active {
  background-color: var(--brand-primary) !important;
  color: #ffffff !important;
}

@media (max-width: 960px) {
  .roles-search {
    max-width: none;
  }
}
</style>

<style>
.role-dialog-card {
  border-radius: 14px;
  overflow: hidden;
}

.role-dialog-title {
  background: linear-gradient(
    90deg,
    rgba(37, 79, 135, 0.1),
    rgba(199, 154, 43, 0.1)
  );
  color: #254f87;
  font-weight: 700;
  font-size: 17px !important;
  padding: 16px 24px !important;
}
</style>
